import React from 'react';
import { Player } from '~/types/player';

import './PlayerProfile.css';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import {
  Avatar,
  Paper,
  Grid2,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Create a custom theme

export interface PlayerProfileProps {
  player: Player | null;
  sport?: string;
  affiliation?: string;
  loading: boolean;
  error: string | null;
}

const theme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: '#f4f4f4', // Light gray background
      paper: '#ffffff', // White for paper components
    },
    text: {
      primary: '#333333', // Dark gray text
      secondary: '#666666', // Lighter gray for secondary text
    },
    primary: {
      main: '#0a66c2', // LinkedIn blue for links and accents
    },
  },
  typography: {
    fontFamily: 'Arial, sans-serif',
    h1: {
      fontSize: '28px',
      fontWeight: 'bold',
    },
    h2: {
      fontSize: '20px',
      fontWeight: 'bold',
    },
    h3: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
    body1: {
      fontSize: '14px',
    },
    body2: {
      fontSize: '12px',
      color: 'text.secondary',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: '#0a66c2',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});

export const PlayerProfile: React.FC<PlayerProfileProps> = ({
  player,
  loading,
  error,
  affiliation,
}) => {
  if (loading)
    return (
      <Box sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}>
        Loading...
      </Box>
    );
  if (error)
    return (
      <Box sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}>
        {error}
      </Box>
    );
  if (!player)
    return (
      <Box sx={{ textAlign: 'center', p: 2, color: 'text.secondary' }}>
        No player data available
      </Box>
    );

  // Find the default sport (first sport for simplicity)
  const defaultSport = player.sports[0];
  const defaultAffiliation = affiliation
    ? defaultSport?.affiliations?.find(aff => aff.id === affiliation)
    : defaultSport?.affiliations?.find(aff => aff.isDefault) ||
      defaultSport?.affiliations?.[0];

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Grid2
          container
          spacing={3}
          sx={{ maxWidth: '1200px', mx: 'auto', px: 2 }}
        >
          {/* Left Column: Bio Information */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Paper>
              {/* Player Image and Name */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                {defaultAffiliation?.player.image.src ? (
                  <Avatar
                    src={defaultAffiliation.player.image.src}
                    alt={
                      defaultAffiliation.player.image.alt ||
                      `${player.firstName} ${player.lastName}'s avatar`
                    }
                    sx={{ width: 120, height: 120, mb: 2 }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mb: 2,
                      bgcolor: '#ccc',
                      fontSize: '48px',
                    }}
                  >
                    {player.firstName.charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <Typography variant="h1">
                  {player.firstName} {player.lastName}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {player.gradYear} Grad | {player.gender}
                </Typography>
              </Box>

              {/* School and Location */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h3">School</Typography>
                <Typography variant="body1">
                  {player.school.name}
                  {player.school.location && `, ${player.school.location}`}
                </Typography>
                {player.school.link && (
                  <Link
                    href={player.school.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit School Website
                  </Link>
                )}
              </Box>

              {/* Physical Stats */}
              {player.physicalStats && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3">Physical Stats</Typography>
                  <Typography variant="body1">
                    Height:{' '}
                    {player.physicalStats.height
                      ? `${player.physicalStats.height} in`
                      : 'N/A'}{' '}
                    | Weight:{' '}
                    {player.physicalStats.weight
                      ? `${player.physicalStats.weight} lbs`
                      : 'N/A'}{' '}
                    | Hand Size:{' '}
                    {player.physicalStats.handSize
                      ? `${player.physicalStats.handSize} in`
                      : 'N/A'}{' '}
                    | Wingspan:{' '}
                    {player.physicalStats.wingSpan
                      ? `${player.physicalStats.wingSpan} in`
                      : 'N/A'}
                  </Typography>
                  <Typography variant="body1">
                    Dominant Hand: {player.physicalStats.dominantHand || 'N/A'}{' '}
                    | Dominant Foot:{' '}
                    {player.physicalStats.dominantFoot || 'N/A'}
                  </Typography>
                </Box>
              )}

              {/* Athletic Metrics */}
              {player.athleticMetrics && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3">Athletic Stats</Typography>
                  <Typography variant="body1">
                    Vertical Jump:{' '}
                    {player.athleticMetrics.verticalJump
                      ? `${player.athleticMetrics.verticalJump} in`
                      : 'N/A'}{' '}
                    | 30-Yard Dash:{' '}
                    {player.athleticMetrics.thirtyYardDash
                      ? `${player.athleticMetrics.thirtyYardDash} s`
                      : 'N/A'}{' '}
                    | 40-Yard Dash:{' '}
                    {player.athleticMetrics.fortyYardDash
                      ? `${player.athleticMetrics.fortyYardDash} s`
                      : 'N/A'}
                  </Typography>
                </Box>
              )}

              {/* Contacts */}
              {player.contacts && player.contacts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3">Contacts</Typography>
                  {player.contacts.map((contact, index) => (
                    <Box key={index} sx={{ mt: 1 }}>
                      <Typography variant="body1">
                        {contact.name || 'N/A'} | Email:{' '}
                        {contact.email || 'N/A'} | Phone:{' '}
                        {contact.phone || 'N/A'}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Social Media */}
              {player.socialMedia && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3">Social Media</Typography>
                  {player.socialMedia.facebook && (
                    <Typography variant="body1">
                      Facebook:{' '}
                      <Link
                        href={player.socialMedia.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {player.socialMedia.facebook}
                      </Link>
                    </Typography>
                  )}
                  {player.socialMedia.instagram && (
                    <Typography variant="body1">
                      Instagram:{' '}
                      <Link
                        href={player.socialMedia.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {player.socialMedia.instagram}
                      </Link>
                    </Typography>
                  )}
                  {player.socialMedia.x && (
                    <Typography variant="body1">
                      X:{' '}
                      <Link
                        href={player.socialMedia.x}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {player.socialMedia.x}
                      </Link>
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          </Grid2>

          {/* Right Column: Accomplishments */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            {/* Sports Section */}
            <Paper sx={{ mb: 2 }}>
              <Typography variant="h2" sx={{ mb: 1 }}>
                Sports
              </Typography>
              {player.sports.map((sport, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="h3">{sport.type}</Typography>
                  {sport.positions && sport.positions.length > 0 && (
                    <Typography variant="body1">
                      Positions: {sport.positions.join(', ')}
                    </Typography>
                  )}
                  {sport.physicalStats && (
                    <Typography variant="body1">
                      Physical Stats:
                      {sport.physicalStats.height
                        ? `${sport.physicalStats.height} in`
                        : 'N/A'}{' '}
                      |
                      {sport.physicalStats.weight
                        ? `${sport.physicalStats.weight} lbs`
                        : 'N/A'}{' '}
                      | Hand Size:{' '}
                      {sport.physicalStats.handSize
                        ? `${sport.physicalStats.handSize} in`
                        : 'N/A'}{' '}
                      | Wingspan:{' '}
                      {sport.physicalStats.wingSpan
                        ? `${sport.physicalStats.wingSpan} in`
                        : 'N/A'}{' '}
                      | Dominant Hand:{' '}
                      {sport.physicalStats.dominantHand || 'N/A'} | Dominant
                      Foot: {sport.physicalStats.dominantFoot || 'N/A'}
                    </Typography>
                  )}
                  {sport.athleticStats && (
                    <Typography variant="body1">
                      Athletic Stats: Vertical Jump:{' '}
                      {sport.athleticStats.verticalJump
                        ? `${sport.athleticStats.verticalJump} in`
                        : 'N/A'}{' '}
                      | 30-Yard Dash:{' '}
                      {sport.athleticStats.thirtyYardDash
                        ? `${sport.athleticStats.thirtyYardDash} s`
                        : 'N/A'}{' '}
                      | 40-Yard Dash:{' '}
                      {sport.athleticStats.fortyYardDash
                        ? `${sport.athleticStats.fortyYardDash} s`
                        : 'N/A'}
                    </Typography>
                  )}
                  {sport.achievements && sport.achievements.length > 0 && (
                    <Typography variant="body1">
                      Achievements: {sport.achievements.join(', ')}
                    </Typography>
                  )}
                  {sport.affiliations && sport.affiliations.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                        Affiliations:
                      </Typography>
                      {sport.affiliations.map(aff => (
                        <Typography key={aff.id} variant="body1">
                          - {aff.type}:{' '}
                          <Link
                            href={aff.link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {aff.name}
                          </Link>
                          {aff.isDefault && ' (Default)'}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {index < player.sports.length - 1 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </Box>
              ))}
            </Paper>

            {/* Academics Section */}
            <Paper sx={{ mb: 2 }}>
              <Typography variant="h2" sx={{ mb: 1 }}>
                Academics
              </Typography>
              <Typography variant="body1">
                GPA: {player.academics.gpa.toFixed(1)}/4.0
              </Typography>
              {player.academics.achievements.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                    Achievements:
                  </Typography>
                  {player.academics.achievements.map((ach, index) => (
                    <Typography key={index} variant="body1">
                      - {ach.name} ({ach.year})
                    </Typography>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid2>
        </Grid2>
      </Box>
    </ThemeProvider>
  );
};
